import { Inject, Injectable, Optional } from '@angular/core';
import { RouterExtensionService } from '../router-extension/router-extension.service';
import { UserEvents, PageviewEventDataProperty, RouterNavigationEndEvent } from '@anecdotes/user-event-data-access';
import { MessageBusService } from '@anecdotes/shared-infra';
import { merge, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, take } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { IS_EXTERNAL_PAGE_TOKEN } from '@anecdotes/auth-core';

const PAGE_OPENED_OUTSIDE_PLATFORM = 'outside the platform';

@Injectable()
export class UserEventService {
  private readonly eventSecretkey = 'user-event-key:';

  constructor(
    private messageHub: MessageBusService,
    private routerExtensionService: RouterExtensionService,
    private router: Router,
    @Optional() @Inject(IS_EXTERNAL_PAGE_TOKEN) private isExternalPage: boolean,
  ) {}

  sendEvent(eventType: string, eventData?: any): void {
    this.messageHub.sendMessage(this.secretKey(eventType), eventData);
  }

  subscribeForEvent<T extends UserEvents>(eventType: T): Observable<any> {
    return this.messageHub.getObservable(this.secretKey(eventType)).pipe(shareReplay({ bufferSize: 1, refCount: true }), take(1));
  }

  subscribeForAllEvents(): Observable<{
    eventType: UserEvents;
    eventData: any;
  }> {
    if (this.isExternalPage) {
      return this.getMessageBusUserEvents();
    }

    return merge(this.getMessageBusUserEvents(), this.getUserRouterNavigationEvents());
  }

  private secretKey(eventType: string): string {
    return `${this.eventSecretkey}${eventType}`;
  }

  private getMessageBusUserEvents(): Observable<{
    eventType: UserEvents;
    eventData: any;
  }> {
    return this.messageHub.getFeedByKeyPrefix(this.eventSecretkey).pipe(
      map((feed) => {
        return {
          eventType: feed.key.toString().replace(this.eventSecretkey, '') as UserEvents,
          eventData: feed.payload,
        };
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    ) as Observable<{ eventType: UserEvents; eventData: any }>;
  }

  private getUserRouterNavigationEvents(): Observable<{
    eventType: UserEvents;
    eventData: any;
  }> {
    return this.routerExtensionService.routerNavigationEndEvent$.pipe(
      startWith(this.getStartNavigationEvent()),
      filter((accumulatedResult) => !!accumulatedResult.currentUrl),
      map((accumulatedResult) => {
        return {
          eventType: UserEvents.PAGEVIEW,
          eventData: {
            [PageviewEventDataProperty.CurrentPageUrl]: accumulatedResult.currentUrl,
            [PageviewEventDataProperty.PreviousPageUrl]: accumulatedResult.previousUrl,
          } as any,
        };
      })
    );
  }

  private getStartNavigationEvent(): RouterNavigationEndEvent {
    const currentUrl = this.router.url.split('?')[0] || '';

    return {
      currentUrl,
      previousUrl: PAGE_OPENED_OUTSIDE_PLATFORM,
      currentEvent: { urlAfterRedirects: currentUrl } as NavigationEnd,
    };
  }
}
